/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  CommunicationEntryPoint,
  EmailDialogConfig,
  emailDialogRoute,
  FilterNames,
  FilterRestService,
  FormStateBuilder,
  MeetingDialogConfig,
  meetingDialogRoute,
  SmsDialogConfig,
  smsDialogRoute,
} from '@gh/builder-shared';
import { BuilderPermission, BuilderPermissionSet, CommunicationType, SystemRoleBit } from '@gh/builder-shared-data';
import {
  AppRouter,
  DateTimeTypes,
  dictionaryItemFromDto,
  DictionaryService,
  filterableForm,
  FilterableFormState,
  getRouteInitData,
  globalDictionaries,
  UserContext,
} from '@gh/common-shared';
import { filterableControl, filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService, FormMode, newFormControl } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';

import { LeadListBusinessService } from '../../resources/lead-list-business.service';
import { LeadListItem } from '../../resources/lead.model';
import {
  salesManagementReportFilterDialogRoute,
} from './sales-management-report-filter-dialog/sales-management-report-filter-dialog.route';
import {
  isSalespersonSelectItemInactive,
  LeadsListFilter,
  SalespersonSelectItem,
} from '../../resources/lead-board.model';
import { filter$, map$ } from '@gh/rx/operators';
import { currentDate, sortByNumberField, sortByStringField } from '@gh/core-util';
// tslint:disable-next-line:import-blacklist
import { combineLatest, Subscription } from 'rxjs';
import { ROW_WARNING_CLASS } from '@gh/common-internal-shared';
import { omit } from 'lodash';

@Component({
  selector: 'gh-lead-list-page',
  template: require('./lead-list.component.html'),
  styles: [require('./lead-list.component.scss')],
})
export class LeadListComponent implements OnInit {
  dataSource: SlicedDataSource<LeadListItem>;
  gridDefinition: LayoutBasedGridDefinition;
  form: FilterableFormState<LeadsListFilter>;
  name = filterableControl<string>(newFormControl(''));
  cursor = newGridPaginationCursor();
  subscription$ = new Subscription();
  notProceedingReasons: any[];
  salesPersonsList: any[];
  stages: any[];
  salesFolderStatuses: any[];
  priorities: any[];
  referredByList: any[];
  willNotComplete = 3;

  get isSalespersonListExist(): boolean {
    return !!this.salesPersonsList.length;
  }

  get isStageListExist(): boolean {
    return !!this.stages && !!this.stages.length;
  }

  get hasEditRules(): boolean {
    return this.builderContext.hasSystemRole(SystemRoleBit.Master) || this.builderContext.hasSystemRole(SystemRoleBit.Corporate) ||
      this.builderContext.hasPermission(BuilderPermission.Sales.Leads.EDIT);
  }

  get hasEventLogViewPermission(): boolean {
    return this.builderContext.hasSystemRole(SystemRoleBit.Master) || this.builderContext.hasSystemRole(SystemRoleBit.Corporate) ||
      this.builderContext.hasPermission(BuilderPermission.EventLog.VIEW);
  }

  get getSalesPersonName(): string {
    if (this.form.get('salesPersonId')?.value) {
      const targetSalesPerson = this.salesPersonsList.find((person) => person.id === this.form.get('salesPersonId')?.value);
      return targetSalesPerson ? targetSalesPerson.name : '';
    }
    return '';
  }

  constructor(private route: ActivatedRoute,
              private routeData: RouteDataService,
              private dictionaryService: DictionaryService,
              private businessService: LeadListBusinessService,
              private router: AppRouter,
              private navigationService: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private userContext: UserContext,
              private filterService: FilterRestService,
              private builderContext: BuilderUserContext,
              private dialogService: DialogService,
              private formStateBuilder: FormStateBuilder,
              ) {
    const date = currentDate();
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        lastContactDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        createdDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        nextActivity: {
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
          cellClass: ({data}: { data: LeadListItem }) =>  data.nextActivity && data.nextActivity <= date ? 'next-activity-warning' : '',
        },
        meetingSfStatusId: {
          valueGetter: (field) => {
            let result = this.salesFolderStatuses.find(
              (status) => parseInt(status.itemCode) === field.data.meetingSfStatusId)?.localization;
            if (parseInt(field.data.meetingSfStatusId) === this.willNotComplete && field.data.notProceedingReason) {
              result += ` (${ this.notProceedingReasons.find((reason) => reason.id.toString() === field.data.notProceedingReason?.toString())?.name})`;
            }
            return result;
          },
        },
        meetingSfPriorityId: {
          valueGetter: (field) =>
            this.priorities.find((priority) => parseInt(priority.itemCode) === field.data.meetingSfPriorityId)?.localization,
        },
        meetingSfStageId: {
          valueGetter: (field) =>
            this.stages.find((stage) => parseInt(stage.itemCode) === field.data.meetingSfStageId)?.localization,
        },
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        rowClassRules: {
          [ROW_WARNING_CLASS]: ({ data }: { data: LeadListItem }) => !data.activeSalesFolderId,
        },
        getRowNodeId: (data: LeadListItem) => data.contactId,
      });
  }

  ngOnInit(): void {
    const {
      filter: initFilter,
      gridLayout,
      salesPersonsList,
      salesFolderStatuses,
      salesPersonId,
    } = getRouteInitData(this.route);
    let isInit = true;
    combineLatest([
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.stage),
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.priority),
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.referredBy),
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.notProceedingReasons).pipe(
        map$((res) => res.map(dictionaryItemFromDto))),
                  ]).subscribe(([stagesDict, priorities, referred, notProceedingReasonsList]) => {
      this.stages =  sortByNumberField(stagesDict, 'itemCode');
      this.priorities = sortByStringField(priorities, 'localization');
      this.referredByList =  sortByNumberField(referred, 'itemCode');
      this.notProceedingReasons = notProceedingReasonsList;
    });
    this.gridDefinition.bind(gridLayout);
    this.salesPersonsList = salesPersonsList;
    let resultSalesPersonId = salesPersonId || initFilter.salesPersonId || '';
    if (salesPersonId ||  initFilter.salesPersonId) {
      const targetSalesPerson = salesPersonsList.find(
        (item: any) => item.salesPersonId === salesPersonId || item.salesPersonId === initFilter.salesPersonId);
      if (!targetSalesPerson) {
        resultSalesPersonId = '';
      }
    }
    this.salesFolderStatuses = salesFolderStatuses;
    this.form = filterableForm(this.formStateBuilder.buildPlain({
                                                                  value: {
                                                                    ...initFilter,
                                                                    salesPersonId: resultSalesPersonId,
                                                                  },
                                                                  children: {
                                                                    searchString: void 0,
                                                                    showInactive: void 0,
                                                                    showActive: void 0,
                                                                    showOverdue: void 0,
                                                                    showCompleted: void 0,
                                                                    salesPersonId: void 0,
                                                                    stage: void 0,
                                                                    priority: void 0,
                                                                    referredBy: void 0,
                                                                  },
                                                                }));

    const tracker = this.routeData
      .tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      const fixedFilter = omit(filter, 'stage', 'priority', 'salesPersonId', 'referredBy') as LeadsListFilter;
      if (filter.stage) {
        fixedFilter.stage = filter.stage;
      }
      if (filter.priority) {
        fixedFilter.priority = filter.priority;
      }
      if (filter.salesPersonId) {
        fixedFilter.salesPersonId = filter.salesPersonId;
      }
      if (filter.referredBy) {
        fixedFilter.referredBy = filter.referredBy;
      }
      if (!isInit) {
        this.filterService.update(FilterNames.leads, fixedFilter).subscribe();
      }
      isInit = false;
      this.dataSource = this.businessService.findAllAsDataSource(fixedFilter);
    });
  }

  onCreate(): void {
    this.router.navigateToChild(['/sales/leads/new'], {
      parentTitle: 'lbl_leads',
    });
  }

  onEdit(lead: LeadListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Sales.Leads.EDIT_VIEW)) {
      this.router.navigateToChild(['/sales/leads', lead.contactId], {
        parentTitle: 'lbl_leads',
      });
    }
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onOpenSalesManagementReport(): void {
    this.dialogService.openWithInput(salesManagementReportFilterDialogRoute, {});
  }

  onLeadBoardClick(): void {
    this.router.navigateToChild(['/sales/leads-board'], {
      parentTitle: 'lbl_leads',
    });
  }

  goToAel(lead?: LeadListItem): void {
    this.navigationService.goToAel({
      label: 'lbl_leads',
      entityType: 'LEAD',
      entityId: lead ? lead.contactId : undefined,
    });
  }

  isSalespersonInactive(salesperson: SalespersonSelectItem): boolean {
    return isSalespersonSelectItemInactive(salesperson);
  }

  onFilterChange(): void {
    this.form.emitFilter();
  }

  onSendSms(contact: LeadListItem): void {
    const phoneList = !!contact.contactPhone ? contact.contactPhone.split(';') : [''];
    let primaryPhone = '';
    if (!!phoneList[0] && phoneList[0].trim() !== '-') {
      primaryPhone = phoneList[0];
    } else if (!!phoneList[1]) {
      primaryPhone = phoneList[1];
    }
    this.dialogService.openWithInput(smsDialogRoute, <SmsDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      contactId: contact.contactId,
      sms: {
        cellPhone: primaryPhone,
        subject: '',
        message: '',
        communicationType: CommunicationType.OutgoingSms,
        attachments: [],
        contactId: contact.contactId,
        isMarkedAsRead: true,
      },
    })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {  });
  }

  onSendEmail(contact: LeadListItem): void {
    const emailList = !!contact.contactEmail ? contact.contactEmail.split(';') : [''];
    let primaryEmail = '';
    if (!!emailList[0] && emailList[0].trim() !== '-') {
      primaryEmail = emailList[0];
    } else if (!!emailList[1]) {
      primaryEmail = emailList[1];
    }
    this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      contactId: contact.contactId,
      gFolderId: contact.customerGoogleFolderId,
      email: {
        email: primaryEmail,
        subject: '',
        message: '',
        communicationType: CommunicationType.Email,
        attachments: [],
        contactId: contact.contactId,
        isMarkedAsRead: true,
      },
    })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {  });
  }

  onAddMeeting(contact: LeadListItem): void {
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint: CommunicationEntryPoint.LeadList,
      salesFolderId: contact.activeSalesFolderId,
      mode: FormMode.CreateMode,
      helpId: 'sales/meetings',
      leadId: contact.contactId,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {});
  }

  onShowCompletedChange(value: any): void {
    if (value.checked) {
      this.form.get('showInactive').setValue(false);
    }
    this.form.emitFilter();
  }

  onShowInactiveChange(value: any): void {
    if (value.checked) {
      this.form.get('showCompleted').setValue(false);
    }
    this.form.emitFilter();
  }
}
